<script setup lang="ts">
import { computed, provide, ref, watch, nextTick, onMounted, onUnmounted } from "vue";
import { storeToRefs } from "pinia";
import { useChatPanelStore } from "@stores/chat/chatPanel.store";
import { useAgentChat } from "./composables/useAgentChat";
import { useChatTheme } from "./composables/useChatTheme";
import { ApprovalResponderKey } from "./composables/approvalResponder";
import { ResponseRatingKey } from "./composables/responseRating";
import { GENERAL_PROMPT_TEMPLATES, PROJECT_PROMPT_TEMPLATES } from "./constants/promptTemplates";
import ChatMessageList from "./components/ChatMessageList.vue";
import ChatInput from "./components/ChatInput.vue";

const chatPanelStore = useChatPanelStore();
const { isOpen } = storeToRefs(chatPanelStore);
const { themeClass } = useChatTheme();

const {
  messages,
  input,
  isLoading,
  isPending,
  errorMessage,
  projectId,
  projectName,
  conversationId,
  handleSubmit,
  newChat,
  retry,
  respondToApproval,
  responseRating,
} = useAgentChat();

// The AI Agent is available with or without a project (RTLCA-4891), and the two cases can't share
// copy: "Ask about your project" and starters that say "this project" are wrong on the AI Agent Skills
// page or the project list, where no projectId is forwarded. Keyed on projectId rather than
// projectName because the name lands a moment later while the project loads.
const hasProject = computed(() => !!projectId.value);
const emptyStateHeading = computed(() => (hasProject.value ? "Ask about your project" : "How can I help?"));
const promptTemplates = computed(() => (hasProject.value ? PROJECT_PROMPT_TEMPLATES : GENERAL_PROMPT_TEMPLATES));
const inputPlaceholder = computed(() => (hasProject.value ? "Ask about your project..." : "Ask a question..."));

// Let inline ToolApprovalCards (rendered deep inside AssistantMessage) answer approval requests.
provide(ApprovalResponderKey, respondToApproval);
// Same shape for the "How was this answer?" prompt that AssistantMessage renders under a picked reply.
provide(ResponseRatingKey, responseRating);

const chatInputRef = ref<InstanceType<typeof ChatInput> | null>(null);

// The window aligns its top-right corner with the "AI Agent" nav button it opens from and
// drops just below it. Falls back to the top-right viewport corner when the button isn't on
// screen (e.g. the mobile nav, where it's collapsed into the hamburger menu).
const anchorStyle = ref<{ top: string; right: string }>({ top: "4rem", right: "1.5rem" });
function updateAnchor() {
  const rect = document.getElementById("assistant-nav-button")?.getBoundingClientRect();
  if (!rect || rect.width === 0) {
    anchorStyle.value = { top: "4rem", right: "1.5rem" };
    return;
  }
  anchorStyle.value = {
    top: `${rect.bottom + 8}px`,
    right: `${Math.max(8, window.innerWidth - rect.right)}px`,
  };
}

// Position + focus whenever the window opens.
watch(isOpen, (open) => {
  if (!open) return;
  // Measure the trigger synchronously — it's always in the DOM, independent of this window —
  // so the window paints at the anchored position with no first-frame jump from the fallback.
  updateAnchor();
  nextTick(() => chatInputRef.value?.focus());
});

// Esc closes the window. Guarded on isOpen so it never swallows Escape elsewhere
// (this component stays mounted for the session, so the listeners are always live).
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape" && isOpen.value) chatPanelStore.close();
}
function onResize() {
  if (isOpen.value) updateAnchor();
}
onMounted(() => {
  window.addEventListener("keydown", onKeydown);
  window.addEventListener("resize", onResize);
});
onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
  window.removeEventListener("resize", onResize);
});

function onSend(skillName?: string) {
  if (!input.value.trim()) return;
  handleSubmit(new Event("submit"), { skillName });
}

function selectPrompt(content: string) {
  if (isLoading.value) return;
  input.value = content;
  onSend();
}
</script>

<template>
  <Teleport to="body">
    <!-- Floating window anchored to the AI Agent nav button (no backdrop): the page behind
         stays visible and interactive. Resetting the conversation on close happens in
         @after-leave so the window animates out with its content intact, then clears. -->
    <Transition
      name="chat-window"
      @after-leave="newChat">
      <div
        v-if="isOpen"
        :class="themeClass"
        :style="anchorStyle"
        class="chat-panel-shell chat-window-card z-overlay rounded-16 fixed flex h-[80vh] max-h-[calc(100vh-5rem)] w-[400px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden border"
        role="dialog"
        aria-label="AI Agent"
        aria-describedby="chat-ai-disclosure">
        <!-- Header -->
        <div class="chat-divider px-px-16 py-px-12 flex shrink-0 items-center justify-between border-b">
          <div class="flex flex-col">
            <span class="chat-text-main t-sh5">AI Agent</span>
            <span
              v-if="projectName"
              class="chat-text-muted t-caption truncate">
              {{ projectName }}
            </span>
          </div>
          <div class="gap-px-8 flex items-center">
            <button
              type="button"
              class="chat-button-subtle rounded-4 px-px-8 py-px-4 t-footnote"
              @click="newChat">
              New chat
            </button>
            <button
              type="button"
              class="chat-button-subtle rounded-4 p-px-4"
              title="Close"
              @click="chatPanelStore.close()">
              <font-awesome-icon
                :icon="['fas', 'xmark']"
                class="text-16" />
            </button>
          </div>
        </div>

        <!-- Empty state with prompt starters -->
        <div
          v-if="messages.length === 0"
          class="gap-px-16 px-px-16 flex flex-1 flex-col items-center justify-center">
          <div class="chat-empty-icon rounded-8 flex h-10 w-10 items-center justify-center">
            <font-awesome-icon
              :icon="['fas', 'leaf']"
              class="chat-empty-icon-svg icon-md" />
          </div>
          <div class="text-center">
            <p class="chat-text-main t-sh6">{{ emptyStateHeading }}</p>
            <p class="chat-text-muted mt-px-4 t-footnote">Select a suggestion or type your own question</p>
          </div>
          <div class="gap-px-8 flex w-full flex-col">
            <button
              v-for="template in promptTemplates"
              :key="template.id"
              type="button"
              class="chat-input-shell chat-prompt-button gap-px-8 rounded-8 px-px-12 py-px-10 duration-fast ease-standard flex w-full items-center border text-left transition-colors"
              @click="selectPrompt(template.content)">
              <font-awesome-icon
                :icon="['fas', 'bolt']"
                class="chat-text-muted icon-xs shrink-0" />
              <span class="chat-text-main t-xs">{{ template.label }}</span>
            </button>
          </div>
        </div>

        <!-- Messages -->
        <ChatMessageList
          v-else
          :messages="messages"
          :is-streaming="isLoading || isPending"
          :error="errorMessage"
          class="min-h-0 flex-1"
          @retry="retry" />

        <!-- Input -->
        <div class="chat-divider px-px-12 py-px-12 shrink-0 border-t">
          <div
            v-if="errorMessage"
            class="chat-error mb-px-8 gap-px-8 rounded-8 px-px-12 py-px-8 flex items-start">
            <font-awesome-icon
              :icon="['fas', 'circle-exclamation']"
              class="chat-error-icon mt-px-2 text-14 shrink-0" />
            <p class="chat-error-text t-footnote">{{ errorMessage }}</p>
          </div>
          <ChatInput
            ref="chatInputRef"
            v-model="input"
            :is-loading="isLoading"
            :conversation-id="conversationId"
            :placeholder="inputPlaceholder"
            @send="onSend" />
        </div>

        <!-- AI disclosure banner -->
        <div
          id="chat-ai-disclosure"
          class="chat-divider px-px-12 py-px-8 shrink-0 border-t"
          role="note"
          aria-label="AI disclosure">
          <p class="chat-text-muted t-footnote leading-normal">
            Responses are generated by AI and may be inaccurate. The AI Agent can read and make changes to your project data.
            Review any changes before relying on them.
          </p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.chat-window-card {
  box-shadow: var(--shadow-modal);
  transform-origin: top right;
}

.chat-window-enter-active,
.chat-window-leave-active {
  transition:
    opacity var(--duration-normal) var(--ease-standard),
    transform var(--duration-normal) var(--ease-standard);
}

.chat-window-enter-from,
.chat-window-leave-to {
  opacity: 0;
  transform: scale(0.97);
}
</style>
